import {
  Text,
  useToast,
  Divider,
  Flex,
  Box,
  Button,
  useDisclosure,
  InputGroup,
  Input,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { Util } from '@finley-technologies/finley-types';
import { commonFormulas } from '@finley-technologies/formula-engine';

import { DeleteConfirmationModal } from '../../../delete/DeleteArrayPropertyButton';
import { LenderPostgresQueryDateView } from './LenderPostgresQueryComponents';
import { QueryInputTypeBadge } from './QueryInputTypeBadge';
import { colors } from 'packages/common-frontend/colors';
import {
  CopyIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { PreviewPostgresQueryButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantPostgresQuery/PreviewPostgresQueryButton';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { AddQueryInputModal } from 'packages/settings/ui/AddQueryInputModal';

type PrincipalBalancePointInTimeFormulaFieldProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (
    query: CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet,
  ) => Promise<void>;
  isSaveLoading: boolean;
  principalBalancePointInTimeQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalancePointInTimeQuery;
  triggerDate?: Util.FullDateStr;
  evaluatedDate?: Util.FullDateStr;
};
const PrincipalBalancePointInTimeFormulaField = ({
  wipConfig,
  fullPropertyKey,
  onSave,
  isSaveLoading,
  principalBalancePointInTimeQuery,
  triggerDate,
  evaluatedDate,
}: PrincipalBalancePointInTimeFormulaFieldProps) => {
  const toast = useToast();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const initialFieldValue =
    principalBalancePointInTimeQuery.parameterConfig.dateFormula.formula;

  const [fieldValue, setFieldValue] = useState<string>(initialFieldValue);

  return (
    <Box
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {isEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
        <>
          <InputGroup mb="24px">
            <Input
              p="24px"
              type="text"
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
            />
          </InputGroup>
          <Flex width="100%" alignItems="center" justifyContent="flex-end">
            <Button
              color={colors.aurora[400]}
              border="2px"
              borderRadius="8px"
              borderColor={colors.aurora[400]}
              backgroundColor="white"
              onClick={() => {
                setFieldValue(initialFieldValue);
                onEditClose();
              }}
              mr="24px"
            >
              Cancel
            </Button>
            <Button
              color="white"
              backgroundColor={colors.aurora[400]}
              isLoading={isSaveLoading}
              onClick={async () => {
                try {
                  const updatedQuery = _.cloneDeep(
                    principalBalancePointInTimeQuery,
                  );
                  updatedQuery.parameterConfig.dateFormula.formula = fieldValue;

                  await onSave(updatedQuery);
                } catch (err) {
                  toast({
                    ...errorToastConfig,
                    id: 'saveError',
                    title: 'Error saving formula. Please try again.',
                  });
                } finally {
                  onEditClose();
                }
              }}
            >
              Save
            </Button>
          </Flex>
        </>
      ) : (
        <LenderPostgresQueryDateView
          initialFieldValue={initialFieldValue}
          onEditOpen={onEditOpen}
          evaluatedDate={evaluatedDate}
          triggerDate={triggerDate}
          dateType="reportingDate"
        />
      )}
    </Box>
  );
};

type LenderPrincipalBalancePointInTimeViewProps = {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalancePointInTimeQuery;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  duplicateParams: {
    isDuplicateQueryOpen: boolean;
    onDuplicateQueryOpen: () => void;
    onDuplicateQueryClose: () => void;
    duplicateQuery: (params: { queryKeyInput: string }) => Promise<void>;
    isDuplicateLoading: boolean;
  };
  deleteParams: {
    isDeleteConfirmationModalOpen: boolean;
    onDeleteConfirmationModalOpen: () => void;
    onDeleteConfirmationModalClose: () => void;
    deleteQuery: () => Promise<void>;
    isDeleteLoading: boolean;
  };
  onSave: (
    query: CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet,
  ) => Promise<void>;
  isSaveLoading: boolean;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  triggerDate?: Util.FullDateStr;
};
export const LenderPrincipalBalancePointInTimeView = ({
  postgresQuery,
  wipConfig,
  fullPropertyKey,
  duplicateParams,
  deleteParams,
  onSave,
  isSaveLoading,
  presetVariables,
  inputDateType,
  triggerDate,
}: LenderPrincipalBalancePointInTimeViewProps) => {
  const {
    isDuplicateQueryOpen,
    onDuplicateQueryOpen,
    onDuplicateQueryClose,
    duplicateQuery,
    isDuplicateLoading,
  } = duplicateParams;
  const {
    isDeleteConfirmationModalOpen,
    onDeleteConfirmationModalOpen,
    onDeleteConfirmationModalClose,
    deleteQuery,
    isDeleteLoading,
  } = deleteParams;

  const evaluatedDate = useMemo(() => {
    if (!triggerDate) {
      return undefined;
    }
    return commonFormulas.evaluateDateFormula({
      dateFormula: postgresQuery.parameterConfig.dateFormula.formula,
      variables: [
        { name: 'triggerDate', value: triggerDate, parseNumber: true },
        ...postgresQuery.variables,
      ],
    });
  }, [postgresQuery, triggerDate]);

  return (
    <Box>
      <Flex alignItems="center" mb="24px" justifyContent="right">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="principalBalancePointInTime"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>
      <Divider />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="64px"
        mb="24px"
      >
        <Text fontWeight={600} fontSize="20px">
          Key
        </Text>
        <QueryInputTypeBadge type="principalBalancePointInTime" />
      </Flex>
      <Text
        color={colors.black[70]}
        p="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {postgresQuery.name}
      </Text>

      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Date
      </Text>
      <PrincipalBalancePointInTimeFormulaField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        principalBalancePointInTimeQuery={postgresQuery}
        evaluatedDate={evaluatedDate}
        triggerDate={triggerDate}
      />

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <PreviewPostgresQueryButton
          wipConfig={wipConfig}
          postgresQuery={postgresQuery}
          inputDateType={inputDateType}
          presetVariables={presetVariables}
          queryType={postgresQuery.type}
          reportingDate={evaluatedDate}
          selectedInputDate={triggerDate}
        />
      )}
    </Box>
  );
};
